import { useState, useEffect, useRef, useMemo } from 'react'
import { Dialog, DialogPanel } from '@headlessui/react'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import './hero.css'
import { gsap } from 'gsap'

const slides = [
  {
    image: './1.jpg',
    statement: 'Revolutionizing Wildlife Protection with AI.'
  },
  {
    image: './3.jpg',
    statement: 'Africa’s Smartest Eyes in the Wild.'
  },
  {
    image: './2.jpg',
    statement: 'Proof That Innovation Can Save Wildlife.'
  },
  {
    image: './4.jpg',
    statement: 'Invest in the Future of Conservation.'
  }
]

const descriptions = [
  'IROD brings cutting-edge artificial intelligence to the frontlines of conservation, enabling real-time animal tracking, poaching detection, and ecosystem monitoring — all without needing internet access.',
  'Powered by computer vision, IROD autonomously identifies and monitors wildlife, sending alerts on threats like poachers or sick animals — giving rangers a 24/7 digital ally in the bush.',
  'IROD is more than tech — it’s a mission to preserve biodiversity through intelligent, scalable solutions that work in Africa’s toughest terrains. The future of conservation starts here.',
  'With your support, IROD can turn proof-of-concept into life-saving deployment — creating safer habitats, empowering local rangers, and building Africa’s own conservation intelligence network.'
]

const textAnimations = [
  'fade-in', // for slide 1
  'slide-up', // for slide 2
  'zoom-in', // for slide 3
  'flip-in', // for slide 4
]

export default function Hero({ onScrollToAbout, navigationLinks = [] }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [textAnimKey, setTextAnimKey] = useState(0)
  const [displayedText, setDisplayedText] = useState(slides[0].statement)
  const [typing, setTyping] = useState(true)
  const typingTimeout = useRef(null)
  const heroRef = useRef()

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => {
        const next = (prev + 1) % slides.length
        return next
      })
    }, 6000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    setTextAnimKey(currentSlide) // force re-mount for animation
  }, [currentSlide])

  useEffect(() => {
    // Typing effect for hero text
    setTyping(true)
    setDisplayedText('')
    let i = 0
    const statement = slides[currentSlide].statement
    function typeChar() {
      setDisplayedText(statement.slice(0, i + 1))
      if (i < statement.length - 1) {
        i++
        typingTimeout.current = setTimeout(typeChar, 30)
      } else {
        setTyping(false)
      }
    }
    typingTimeout.current = setTimeout(typeChar, 100)
    return () => clearTimeout(typingTimeout.current)
  }, [currentSlide])

  // Memoize split and shuffled words for each slide
  const animatedWords = useMemo(() => {
    const words = descriptions[currentSlide].split(' ')
    // Shuffle indices for random animation order
    const indices = words.map((_, i) => i)
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[indices[i], indices[j]] = [indices[j], indices[i]]
    }
    return indices.map((order, i) => ({
      word: words[i],
      delay: 0.08 * indices.indexOf(i)
    }))
  }, [currentSlide, descriptions])

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY
      const vh = window.innerHeight
      // Parallax: hero moves up and fades out as you scroll
      gsap.to(heroRef.current, {
        y: -scrollY * 0.25,
        opacity: 1 - Math.min(scrollY / (vh * 0.8), 1),
        scale: 1 - Math.min(scrollY / (vh * 2.5), 0.08),
        duration: 0.7,
        overwrite: 'auto',
        ease: 'power2.out',
      })
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <div className="hero-container" ref={heroRef}>
      <div className="hero-background-slider">
        <div className="slider-track" style={{ transform: `translateY(-${currentSlide * 100}vh)` }}>
          {slides.map((slide, idx) => (
            <div className={`slider-slide${idx === currentSlide ? ' active' : ''}`} key={idx}>
              <img src={slide.image} className="slider-image" alt="" />
            </div>
          ))}
        </div>
      </div>

      <header className="hero-header">
        <nav className="hero-nav" aria-label="Global">
          <div className="nav-left">
            <a href="#" className="logo-link">
              <span className="sr-only">Your Company</span>
              <img
                alt=""
                src="./logo.png"
                className="logo-img"
              />
            </a>
          </div>

          <div className="mobile-menu-button-container">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="mobile-menu-button"
              aria-label="Open main menu"
            >
              <Bars3Icon className="icon-size" aria-hidden="true" />
            </button>
          </div>

          <div className="nav-links">
            {navigationLinks.map((item) =>
              item.type === 'route' ? (
                <a key={item.name} href={item.href} className="nav-link" style={{ color: item.name === 'Partner Us' ? '#6366f1' : undefined }}>
                  {item.name}
                </a>
              ) : (
                <a key={item.name} href={item.href} className="nav-link">
                  {item.name}
                </a>
              )
            )}
          </div>

          <div className="nav-login">
            <a href="#" className="nav-link">
              {/* Log in <span aria-hidden="true">&rarr;</span> */}
            </a>
          </div>
        </nav>

        <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="mobile-menu-dialog" as="div">
          <div aria-hidden="true" />
          <DialogPanel className="mobile-menu-panel">
            <div className="mobile-menu-header">
              <a href="#" className="logo-link">
                <span className="sr-only">Your Company</span>
                <img
                  alt=""
                  src="./logo.png"
                  className="logo-img"
                />
              </a>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="close-menu-button"
                aria-label="Close menu"
              >
                <XMarkIcon className="icon-size" aria-hidden="true" />
              </button>
            </div>

            <div className="mobile-menu-content">
              <nav>
                <ul className="nav-list">
                  {navigationLinks.map((item) => (
                    <li key={item.name}>
                      <a href={item.href} className="mobile-nav-link" style={{ color: item.name === 'Partner Us' ? '#6366f1' : undefined }}>
                        {item.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="mobile-login">
                <a href="#" className="mobile-nav-link">
                  Log in
                </a>
              </div>
            </div>
          </DialogPanel>
        </Dialog>
      </header>

      <main className="hero-main">
        <div className="background-shape-top" aria-hidden="true" />
        <div className="hero-content">
          <h1
            key={textAnimKey}
            className={`hero-title typewriter`}
          >
            {displayedText}
            <span className={`typewriter-cursor${typing ? ' typing' : ''}`}>|</span>
          </h1>
          <p className="hero-description fade-in-up">
            <span className="random-words-animate">
              {animatedWords.map(({ word, delay }, i) => (
                 <span
                  key={i}
                  style={{
                    animationDelay: `${delay + 0.2}s`,
                    display: 'inline-block',
                    marginRight: '0.35em',
                    marginBottom: '0.15em',
                    minWidth: '0.5em',
                  }}
                >
                  {word}{' '}
                </span>
              ))}
            </span>
          </p>
          <div className="hero-buttons">
            <a href="#contact" className="btn-primary" onClick={e => {
              e.preventDefault();
              const contact = document.getElementById('contact');
              if (contact) {
                const rect = contact.getBoundingClientRect();
                const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                const targetY = rect.top + scrollTop - 40;
                gsap.to({}, {
                  duration: 1.2,
                  ease: 'power2.inOut',
                  onUpdate: function() {
                    const progress = this.progress();
                    const currentY = window.scrollY;
                    const newY = currentY + (targetY - currentY) * progress;
                    window.scrollTo(0, newY);
                  }
                });
              }
            }}>
              Get started
            </a>
            <a href="#features" className="btn-secondary">Learn more →</a>
          </div>
        </div>
        <div className="background-shape-bottom" aria-hidden="true" />
      </main>
    </div>
  )
}
